import type { Column } from "@shared/types/data-table"
import { AppStatusBadge } from "@shared/components/ui/status-badge/AppStatusBadge"
import { Shield } from "lucide-react"
import { 
  TextCell, 
  IdCell, 
  CurrencyCell, 
  DynamicDateCell, 
  IconTextCell 
} from "../cells/common-cells"
import { TruncatedCell } from "../cells/TruncatedCell"

/**
 * Factory functions for creating standardized table columns
 * These functions provide consistent styling and behavior across all tables
 */
export function createIdColumn<T extends { id: string | number }>(key = "id"): Column<T> {
  return {
    key,
    header: "ID",
    width: 120,
    render: (item: T) => <IdCell id={item.id} />
  }
}

export function createStatusColumn<T extends { status?: string }>(entityType: string, key = "status"): Column<T> {
  return {
    key,
    header: "Status",
    width: 120,
    render: (item: T) => <AppStatusBadge entityType={entityType} status={item.status || "unknown"} />
  }
}

/**
 * Creates a text column with consistent styling and optional value transformation
 * @param key - Property key from the entity type
 * @param header - Display name for the column header
 * @param width - Column width in pixels (default: 120)
 * @param className - Additional CSS classes for styling
 * @param placeholder - Text to show when value is empty (default: "—")
 * @param transform - Optional function to transform the value before display
 */
export function createTextColumn<T>(
  key: keyof T,
  header: string,
  width: number = 120,
  className?: string,
  placeholder?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transform?: (value: any) => string
): Column<T> {
  return {
    key: String(key),
    header,
    width,
    render: (item: T) => {
      const value = item[key]
      const displayValue = transform ? transform(value) : (value as string)
      return (
        <TextCell 
          value={displayValue} 
          className={className}
          placeholder={placeholder}
          maxWidth={width}
        />
      )
    }
  }
}

export function createCurrencyColumn<T>(key: keyof T, header: string, width?: number): Column<T> {
  return {
    key: String(key),
    header,
    width: width || 120,
    align: "right",
    render: (item: T) => {
      // Extract currency and decimal info from entity if available
      const record = item as Record<string, unknown>;
      const currency = (record.currency as string) || 'USD';
      const decimals = record.decimal as number | undefined;
      
      return (
        <CurrencyCell
          amount={item[key] as number || 0}
          currency={currency}
          decimals={decimals}
          align="right"
          className="text-sm min-w-[68px]"
        />
      );
    }
  }
}

/**
 * Creates a date column that can display full datetime or date-only
 * @param key - Property key containing the date value
 * @param header - Display name for the column header
 * @param width - Column width in pixels (default: 150)
 * @param showTime - Whether to include time portion (default: true)
 *   - true: "Jan 15, 2024 2:30 pm"
 *   - false: "Jan 15, 2024"
 */
export function createDateColumn<T>(
  key: keyof T, 
  header: string, 
  width?: number,
  showTime: boolean = true
): Column<T> {
  return {
    key: String(key),
    header,
    width: width || 150,
    render: (item: T) => (
      <DynamicDateCell
        timestamp={item[key] as string}
        placeholder="N/A"
        className="text-muted-foreground group-hover:text-foreground"
        showTime={showTime}
      />
    )
  }
}

export function createPaymentMethodColumn<T extends { paymentMethod?: string }>(width = 150): Column<T> {
  return createTextColumn("paymentMethod" as keyof T, "Payment method", width, "text-muted-foreground group-hover:text-foreground font-normal")
}

export function createLocationColumn<T extends { location?: string | { name?: string; city?: string; state?: string } }>(
  width = 200
): Column<T> {
  return createTruncatedColumn(
    "location" as keyof T,
    "Location", 
    width,
    (item: T) => {
      const location = typeof item.location === 'string' 
        ? { name: item.location, city: '', state: '' } 
        : item.location
      return location?.name 
        ? `${location.name}${location.city ? ` • ${location.city}` : ''}${location.state ? `, ${location.state}` : ''}` 
        : "Unknown"
    }
  )
}

export function createRiskColumn<T extends { riskLevel?: string; riskScore?: number }>(
  width = 100
): Column<T> {
  return createIconTextColumn(
    "risk" as keyof T,
    "Risk",
    width,
    <Shield className="h-3 w-3 text-muted-foreground" />,
    (item: T) => item.riskLevel || "low"
  )
}


/**
 * Creates a column with text truncation and custom value extraction
 * Useful for complex data that needs formatting before display
 * @param key - Property key (used for sorting/identification)
 * @param header - Display name for the column header
 * @param width - Column width in pixels, also sets truncation width
 * @param getValue - Function to extract/format the display value from the item
 * @param className - Additional CSS classes for the cell
 */
export function createTruncatedColumn<T>(
  key: keyof T,
  header: string,
  width: number,
  getValue: (item: T) => string,
  className?: string
): Column<T> {
  return {
    key: String(key),
    header,
    width,
    render: (item: T) => {
      const displayValue = getValue(item)
      return (
        <TruncatedCell
          maxWidth={width}
          className={className}
          title={displayValue}
        >
          {displayValue}
        </TruncatedCell>
      )
    }
  }
}

/**
 * Creates a completely custom column with full control over rendering
 * Use when built-in column types don't meet your needs
 * @param key - Property key (used for sorting/identification)
 * @param header - Display name for the column header
 * @param width - Column width in pixels
 * @param renderFn - Custom function to render the cell content
 */
export function createCustomColumn<T>(
  key: keyof T,
  header: string,
  width: number,
  renderFn: (item: T) => React.ReactNode
): Column<T> {
  return {
    key: String(key),
    header,
    width,
    render: renderFn
  }
}

/**
 * Creates a column that displays an icon alongside text content
 * Commonly used for status indicators, types, or categories
 * @param key - Property key containing the text value
 * @param header - Display name for the column header
 * @param width - Column width in pixels
 * @param icon - React element to display (e.g., Lucide icon)
 * @param getValue - Optional function to format the text value
 * @param iconClassName - Additional CSS classes for the icon
 */
export function createIconTextColumn<T>(
  key: keyof T,
  header: string,
  width: number,
  icon: React.ReactNode,
  getValue?: (item: T) => string,
  iconClassName?: string
): Column<T> {
  return {
    key: String(key),
    header,
    width,
    render: (item: T) => {
      const value = getValue ? getValue(item) : (item[key] as string)
      return (
        <IconTextCell 
          icon={icon} 
          iconClassName={iconClassName}
        >
          <span>{value}</span>
        </IconTextCell>
      )
    }
  }
}